import type { AnyAction, Dispatch } from 'redux';

import { translate } from '@front/i18n';

import { user as userApi } from '../../api/common';
import type { DeviceDetails, User } from '../../types/commons';
import type { DataPayload, ErrorPayload, PayloadAction } from '../../types/state';
import { actionTypes } from '../actions';
import exporter from '../exporter';
import {
  fetchInitialState as defaultState,
  onFetchError,
  onFetchStart,
  onUpdateError,
  onUpdateStart,
  onUpdateSuccess,
  updateInitialState,
} from '../object';
import { onQueryError, onQueryStart, onQuerySuccess, queryInitialState } from '../queries';
import { dispatchError } from '../utils';
import type { UserState } from './types';

const t = translate('oauth');

// TODO: Move to customer package

// ~~~~~~~~~~ CONSTANTS ~~~~~~~~~~

export const SIGNOUT = 'USER_SIGNOUT';
export const FETCH_START = 'USER_START_SIGNIN';
export const FETCH_SUCCESS = 'USER_FETCH_SUCCESS';
export const FETCH_ERROR = 'USER_FETCH_ERROR';
export const SET_INITIAL_SUCCESS = 'USER_SET_INITIAL_SUCCESS';
export const SET_INITIAL_FAILURE = 'USER_SET_INITIAL_FAILURE';
export const PASSWORD_RESET_INIT = 'USER_PASSWORD_RESET_INIT';
export const PASSWORD_RESET_START = 'USER_PASSWORD_RESET_START';
export const PASSWORD_RESET_SUCCESS = 'USER_PASSWORD_RESET_SUCCESS';
export const PASSWORD_RESET_ERROR = 'USER_PASSWORD_RESET_ERROR';
export const UPDATE_LAST_POTENTIAL_ZIP_CODE = 'USER_UPDATE_LAST_POTENTIAL_ZIP_CODE';
export const OAUTH_CONNECT = actionTypes('USER_OAUTH_CONNECT');
export const SET_SESSION_HASH = 'USER_SET_SESSION_HASH';
export const UPDATE = actionTypes('USER_UPDATE');
export const USER_DELETE = actionTypes('USER_DELETE');
export const FETCH_MEMBERSHIP = actionTypes('USER_FETCH_MEMBERSHIP');

// ~~~~~~~~~~ SELECTORS ~~~~~~~~~~

/*
 * TODO: The person in charge of refactoring the current object in object ducks
 * will need to take care of this selector and returns this directly : state.users.current
 */
export const getCurrentUser = ({ users }: { users: UserState }) => {
  const user = users?.current;

  return user && 'email' in user ? user : undefined;
};

// ~~~~~~~~~~ ACTIONS ~~~~~~~~~~

export const update = (payload: Partial<User & DeviceDetails>) => async (dispatch: Dispatch) => {
  dispatch({ type: UPDATE.START });
  try {
    const user = await userApi.update(payload);
    dispatch({ type: UPDATE.SUCCESS, payload: user });
  } catch (error) {
    dispatchError(dispatch, UPDATE.ERROR, error);
  }
};

export const fetchMembership = () => async (dispatch: Dispatch) => {
  dispatch({ type: FETCH_MEMBERSHIP.START });
  try {
    const user = await userApi.get();
    dispatch({ type: FETCH_MEMBERSHIP.SUCCESS, payload: user });
  } catch (error) {
    dispatchError(dispatch, FETCH_MEMBERSHIP.ERROR, error);
  }
};

// ~~~~~~~~~~ REDUCER ~~~~~~~~~~
export const initialState: UserState = {
  ...defaultState,
  ...updateInitialState,
  isSignedIn: false,
  isReady: false,
  passwordReset: false,
  message: '',
  sessionHash: undefined,
  isLeavingApp: false,
  thirdPartyAuth: {
    provider: undefined,
    authorized: false,
    userId: undefined,
    accessToken: undefined,
    partialUser: {},
    errors: [],
  },
  deletion: queryInitialState,
};

const onSignout = (state: UserState) => ({
  ...state,
  isLoading: false,
  isSignedIn: false,
  errors: [],
  current: {},
});

// Specific onFetchSucess here because we use the isSignedIn flag
const onFetchSuccess = (state: UserState, action: PayloadAction<User>) => ({
  ...state,
  isLoading: false,
  isSignedIn: true,
  current: action.payload,
  errors: [],
});

const onSetInitialFailure = (state: UserState) => ({ ...state, isSignedIn: false, isReady: true });

const onSetInitialSuccess = (state: UserState, action: AnyAction) => ({
  ...state,
  isSignedIn: true,
  isReady: true,
  current: action.payload,
});

const onPasswordResetInit = (state: UserState) => ({
  ...state,
  passwordReset: false,
  errors: [],
  message: '',
});

const onPasswordResetStart = (state: UserState) => ({ ...state, isLoading: true, errors: [] });

const onPasswordResetSuccess = (state: UserState, action: AnyAction) => {
  const { message } = action.payload;
  return { ...state, isLoading: false, passwordReset: true, errors: [], message };
};

const onPasswordResetError = (state: UserState, action: AnyAction) => {
  const { errors } = action.payload;
  return { ...state, errors, isLoading: false };
};

const onUpdateLastPotentialZipCode = (state: UserState, action: AnyAction) => {
  const { zipCode } = action.payload;
  const { current } = state;
  return { ...state, current: { ...current, last_potential_zip_code: zipCode } };
};

// TODO: Add tests for this reducer
const onOAuthStart = (state: UserState, { payload }: AnyAction) => ({
  ...state,
  ...initialState,
  isLoading: true,
  thirdPartyAuth: {
    ...state.thirdPartyAuth,
    ...payload,
    errors: [],
  },
});

const onOAuthSuccess = (state: UserState) => ({
  ...state,
  isLoading: false,
  thirdPartyAuth: {
    ...state.thirdPartyAuth,
    authorized: true,
  },
});

const onOAuthError = (state: UserState, action: AnyAction) => {
  const {
    payload: {
      errorResponse: { status },
      errorResponse,
      redirectToSignup,
    },
  } = action;

  if (status === 422) {
    if (redirectToSignup) {
      const { response: partialUser } = errorResponse;
      return {
        ...state,
        isLoading: false,
        thirdPartyAuth: {
          ...state.thirdPartyAuth,
          partialUser: {
            ...partialUser,
            cguChecked: true,
          },
          authorized: !!partialUser,
        },
      };
    }
    return {
      ...state,
      isLoading: false,
      thirdPartyAuth: {
        ...state.thirdPartyAuth,
        errors: [
          t('.errors.third_party_connection', {
            provider: t(`.providers.${state.thirdPartyAuth.provider}`),
          }),
        ],
      },
    };
  }
  return {
    ...state,
    isLoading: false,
    thirdPartyAuth: {
      ...state.thirdPartyAuth,
      errors: [t('.errors.process')],
    },
  };
};

const onDeleteStart = (state: UserState) => ({
  ...state,
  deletion: onQueryStart(state.deletion),
});

const onDeleteError = (state: UserState, action: PayloadAction<ErrorPayload>) => ({
  ...state,
  deletion: onQueryError(state.deletion, action),
});

const onDeleteSuccess = (state: UserState, action: PayloadAction<DataPayload<any>>) => ({
  ...state,
  isSignedIn: false,
  current: {},
  deletion: onQuerySuccess(state.deletion, action),
});

const onFetchMembershipSuccess = (state: UserState, action: PayloadAction<User>) => ({
  ...state,
  isLoading: false,
  errors: [],
  current: {
    ...state.current,
    membership: action.payload.membership,
    membership_trial_used: action.payload.membership_trial_used,
  },
});

export const handlers = {
  [SIGNOUT]: onSignout,
  [FETCH_START]: onFetchStart,
  [FETCH_ERROR]: onFetchError,
  [FETCH_SUCCESS]: onFetchSuccess,
  [SET_INITIAL_FAILURE]: onSetInitialFailure,
  [SET_INITIAL_SUCCESS]: onSetInitialSuccess,
  [PASSWORD_RESET_INIT]: onPasswordResetInit,
  [PASSWORD_RESET_START]: onPasswordResetStart,
  [PASSWORD_RESET_SUCCESS]: onPasswordResetSuccess,
  [PASSWORD_RESET_ERROR]: onPasswordResetError,
  [UPDATE_LAST_POTENTIAL_ZIP_CODE]: onUpdateLastPotentialZipCode,
  [OAUTH_CONNECT.START]: onOAuthStart,
  [OAUTH_CONNECT.SUCCESS]: onOAuthSuccess,
  [OAUTH_CONNECT.ERROR]: onOAuthError,
  [UPDATE.START]: onUpdateStart,
  [UPDATE.SUCCESS]: onUpdateSuccess,
  [UPDATE.ERROR]: onUpdateError,
  [USER_DELETE.START]: onDeleteStart,
  [USER_DELETE.ERROR]: onDeleteError,
  [USER_DELETE.SUCCESS]: onDeleteSuccess,
  [FETCH_MEMBERSHIP.START]: onFetchStart,
  [FETCH_MEMBERSHIP.ERROR]: onFetchError,
  [FETCH_MEMBERSHIP.SUCCESS]: onFetchMembershipSuccess,
};

export const reducer = exporter<UserState>(initialState, handlers);
